import { Pause, Play, Rewind, Volume, Volume1, Volume2, VolumeOff } from 'lucide-solid'
// AudioPlayer.tsx
import { type JSX, splitProps } from 'solid-js'
import { css } from 'styled-system/css'
import { Box } from 'styled-system/jsx'

// ---- TSX: make TypeScript happy with custom elements
declare module 'solid-js' {
	namespace JSX {
		interface IntrinsicElements {
			'media-controller': any
			'media-control-bar': any
			'media-play-button': any
			'media-seek-backward-button': any
			'media-seek-forward-button': any
			'media-time-range': any
			'media-time-display': any
			'media-mute-button': any
			'media-volume-range': any
			'media-playback-rate-button': any
		}
	}
}

type Props = JSX.AudioHTMLAttributes<HTMLAudioElement> & {
	class?: string
	containerClass?: string
	children?: JSX.Element
}

export function AudioPlayer(allProps: Props) {
	const [props, rest] = splitProps(allProps, ['class', 'containerClass', 'children'])

	const controllerClass = css({
		w: '200px',
		minH: '60px',
		bg: 'bg.subtle',
		border: 'solid px',
		borderColor: 'bg.canvas',
	})

	// Layout row that wraps gracefully on small screens
	const barClass = css({
		display: 'grid',
		w: '200px',
		gridTemplateRows: '1fr 1fr',
		h: '60px',
		alignItems: 'center',
		justifyContent: 'center',
		alignContent: 'center',
		justifyItems: 'center',
	})

	// Time range grows in the middle; shrinks on small screens
	const timeRangeClass = css({})

	// Hide some “nice to have” bits on small screens
	const hideOnSm = css({
		display: { base: 'none', md: 'inline-flex' },
	})

	const volumeWidth = css({})

	const rightCluster = css({})

	const controlVars = css({
		'--media-control-background': 'transparent',
		'--media-control-hover-background': 'transparent',
		'--media-icon-color': 'colors.fg.default',
		'--media-text-color': 'colors.fg.default',
		'--media-range-bar-color': 'colors.fg.default',
		'--media-range-thumb-background': 'colors.tscGreen.10',
		_dark: {
			'--media-control-background': 'transparent',
			'--media-control-hover-background': 'transparent',
		},
	})

	return (
		<Box border='solid 1px' borderColor='bg.emphasized' borderRadius='md' pt={1} pr={1}>
			<media-controller
				autohide="-1"
				class={[controllerClass, controlVars, props.containerClass]
					.filter(Boolean)
					.join(' ')}
				aria-label="Audio player"
			>
				<audio
					slot="media"
					preload="metadata"
					{...rest}
				>
					{props.children}
				</audio>

				<media-control-bar class={barClass}>
					<Box>
						<media-play-button
							aria-label="Play/Pause"
							notooltip
						>
							<div slot="play">
								<Play height="15px" />
							</div>
							<div slot="pause">
								<Pause height="15px" />
							</div>
						</media-play-button>
						<media-time-range
							notooltip
							class={css({
								height: '100%',
							})}
							aria-label="Seek"
						/>
						<media-time-display
							notooltip
							class={hideOnSm}
							showduration
							aria-label="Time"
						/>
					</Box>

					<Box class={rightCluster}>
						<media-mute-button
							notooltip
							class={css({
								height: '100%',
							})}
							aria-label="Mute"
						>
							<div slot="off">
								<VolumeOff height="15px" />
							</div>
							<div slot="low">
								<Volume height="15px" />
							</div>

							<div slot="medium">
								<Volume1 height="15px" />
							</div>

							<div slot="high">
								<Volume2 height="15px" />
							</div>
						</media-mute-button>
						<media-volume-range
							notooltip
							class={css({
								height: '100%',
							})}
							aria-label="Volume"
						/>
						<media-playback-rate-button
							notooltip
							class={css({
								height: '100%',
							})}
							aria-label="Playback speed"
							playbackrates="0.5,0.75,1,1.25,1.5,2"
						/>
						<media-seek-backward-button
							notooltip
							clsss={css({
								height: '100%',
							})}
							seconds={10}
							aria-label="Back 10 seconds"
						>
							<div slot="icon">
								<Rewind height="15px" />
							</div>
						</media-seek-backward-button>
					</Box>
				</media-control-bar>
			</media-controller>
		</Box>
	)
}
